import { Fragment, useMemo, useState } from 'react';

import { faPenToSquare, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { useSetAtom } from 'jotai';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { type FindingValidations } from '@/@types/persistUserValidations';
import { addMessage } from '@/app/snackbar/SnackbarActions';
import { isHoveringUserValidationFindingAtom } from '@/atoms/atoms';
import type { AREA_OF_CONCERN } from '@/constants';
import { useUserAnnotations } from '@/react-query/useCreateUserAnnotation';
import {
  generateValidationRequest,
  useUpdateUserValidation,
} from '@/react-query/useUpdateUserValidation';
import { useRestrictClinicalInteractionsForDirectors } from '@/utils/hooks';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';
import { PracticeFeatureFlagKey, useFeatureFlag } from '@/utils/user';

import { flattenRBLLines } from '../../annotation-utils/annotation-utils';
import {
  deleteUserAnnotationAction,
  updateFindingAction,
} from '../../AnnotatorActions';
import {
  type FindingObject,
  type Models,
  type Patient,
  type UserAnnotation,
} from '../../types';
import {
  getCariesSubTypedFindings,
  useGetModelFeatureFlags,
} from '../../utils';

import { filterForUserValidationFindings } from './helpers';
import { ValidationAnnotation } from './ValidationAnnotation';
import { ValidationFinding } from './ValidationFinding';

type FindingsResult = Record<
  keyof Models | typeof AREA_OF_CONCERN,
  { findings: FindingObject[]; annotations: UserAnnotation[] }
>;

type FindingsResultEntry = [
  keyof Models | typeof AREA_OF_CONCERN,
  { findings: FindingObject[]; annotations: UserAnnotation[] },
];

export const ManageFindings = () => {
  const [isOpen, setIsOpen] = useState(false);
  const setIsHoveringUserValidationFinding = useSetAtom(
    isHoveringUserValidationFindingAtom,
  );
  const { id: selectedPatientId, imageId: selectedImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();
  const user = useSelector((state: any) => state.login.user);
  const rblSetting = user?.practice?.rblSettings?.templateName;
  const annotatorState = useSelector((state: any) => state.annotator) || {};
  const currentPatient: Partial<Patient> =
    annotatorState?.patients?.[selectedPatientId] || {};
  const activeImage = Object.values(currentPatient.images ?? {}).find(
    ({ objectId }) => objectId === selectedImageId,
  );

  const isSalesDemo = !!useFeatureFlag(PracticeFeatureFlagKey.IS_SALES_DEMO)
    ?.value;
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const dispatch = useDispatch();
  const { mutate: updateUserValidation } = useUpdateUserValidation();
  const {
    removeUserAnnotation: { mutate: deleteUserAnnotation },
  } = useUserAnnotations();
  const modelFeatureFlags = useGetModelFeatureFlags();
  const isRestrictClinicalInteractions =
    useRestrictClinicalInteractionsForDirectors();

  const handleChangeUserAnnotation = (
    finding: FindingObject,
    value: FindingValidations,
  ) => {
    if (!activeImage?.models) {
      return;
    }

    trackEvent(mixpanelEvents.updateAiValidation, {
      fileName: activeImage.fileName,
      objectId: activeImage.objectId,
      prevValidation: finding.validation,
      state: activeImage.analysisState,
      validation: value,
    });

    if (isSalesDemo) {
      // Update finding on the frontend without API call
      updateFindingAction(selectedPatientId, activeImage, finding, value);
      return;
    }

    const payload = generateValidationRequest(
      finding as FindingObject,
      activeImage.analysisId
        ? String(activeImage.analysisId)
        : activeImage.imageId,
      value,
    );

    updateUserValidation(payload, {
      onError: () =>
        addMessage(
          t`Sorry, we could not save your changes. Please try again later`,
          'error',
          2000,
        ),
      onSettled: () => {
        updateFindingAction(selectedPatientId, activeImage, finding, value);
      },
    });
  };

  const handleDeleteUserAnnotation = (annotation: UserAnnotation) => {
    if (typeof activeImage?.imageManifestId !== 'number') {
      dispatch(
        addMessage(
          t`Sorry, we could not save your changes. Please try again later`,
          'error',
          2000,
        ),
      );
      return;
    }

    trackEvent(mixpanelEvents.deleteUserAnnotation, {
      prevValidation: annotation.validation,
      fileName: activeImage?.fileName,
      objectId: activeImage?.objectId,
      state: activeImage?.analysisState,
    });

    const payload = {
      imageManifestId: activeImage.imageManifestId,
      annotationId: annotation.annotationId,
    };

    deleteUserAnnotation(payload, {
      onSuccess: () => {
        dispatch(
          deleteUserAnnotationAction(
            selectedPatientId,
            annotation,
            currentPatient.images,
            annotatorState?.imageAnalyses ?? {},
          ),
        );
      },
      onError: () => {
        dispatch(
          addMessage(
            t`Sorry, we could not save your changes. Please try again later`,
            'error',
            2000,
          ),
        );
      },
    });
  };

  const handleMouseEnter = (finding: FindingObject | UserAnnotation) => {
    // Setting atom to ensure that we re-draw findings on hover change
    setIsHoveringUserValidationFinding(true);
    updateFindingAction(
      selectedPatientId,
      activeImage,
      { ...finding, hover: true },
      undefined,
    );
  };

  const handleMouseLeave = (finding: FindingObject | UserAnnotation) => {
    // Setting atom to ensure that we re-draw findings on hover change
    setIsHoveringUserValidationFinding(false);
    updateFindingAction(
      selectedPatientId,
      activeImage,
      { ...finding, hover: false },
      undefined,
    );
  };

  const displayFindings = useMemo(() => {
    return Object.entries(activeImage?.models ?? {}).reduce<FindingsResult>(
      (acc, [key, value]) => {
        let findings = value?.findings ?? [];

        switch (key as keyof Models) {
          case 'caries':
            if (modelFeatureFlags.isCariesSubclassEnabled) {
              const { caries, areasOfConcern } = getCariesSubTypedFindings(
                value?.findings ?? [],
              );
              findings = caries;
              acc.area_of_concern = {
                findings: areasOfConcern,
                annotations: [],
              };
            }
            break;
          case 'radiographic_boneloss':
            findings = flattenRBLLines(findings);
            break;
          default:
            break;
        }

        const userAnnotations: UserAnnotation[] = (
          activeImage?.userAnnotations ?? []
        ).filter((annotation) => {
          switch (key as keyof Models) {
            case 'caries':
              return annotation.type === 'caries';
            case 'periapical_radiolucency':
              return annotation.type === 'periapicalRadiolucency';
            case 'radiographic_boneloss':
              return annotation.type === 'radiographicBoneloss';
            case 'calculus':
              return annotation.type === 'calculus';
            default:
              return false;
          }
        });

        acc[key as keyof Models] = {
          findings: filterForUserValidationFindings({
            findings,
            findingType: key as keyof Models,
            isPeriapical: activeImage?.detectedImageType === 'PERIAPICAL',
            modelFeatureFlags,
            rblSetting,
          }) as FindingObject[],
          annotations: filterForUserValidationFindings({
            findings: userAnnotations,
            findingType: key as keyof Models,
            modelFeatureFlags,
            rblSetting,
            isPeriapical: activeImage?.detectedImageType === 'PERIAPICAL',
          }) as UserAnnotation[],
        };
        return acc;
      },
      {} as FindingsResult,
    );
  }, [
    activeImage?.detectedImageType,
    activeImage?.models,
    activeImage?.userAnnotations,
    modelFeatureFlags,
    rblSetting,
  ]);

  const hasUserValidatedFindings = useMemo(() => {
    return Object.values(displayFindings).some(
      ({ findings, annotations }) => findings.length || annotations.length,
    );
  }, [displayFindings]);

  const findingsEntries = Object.entries(
    displayFindings,
  ) as FindingsResultEntry[];

  if (!hasUserValidatedFindings) {
    return null;
  }

  // TODO: Only had <hr /> between system and user-created findings rather than every item

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        onClick={() => setIsOpen(true)}
        data-testid="manage-findings-trigger"
      >
        <div
          className={classNames(
            'vai-border vai-rounded-3xl vai-px-3 vai-flex vai-items-center vai-gap-2',
            {
              'vai-bg-zinc-700 vai-border-transparent': !isOpen,
              'vai-bg-zinc-800 vai-border-zinc-700': isOpen,
            },
          )}
        >
          <p className="vai-font-inter">
            <Trans>Manage Findings</Trans>
          </p>
          <FontAwesomeIcon icon={faPenToSquare} />
        </div>
      </PopoverTrigger>
      <PopoverContent
        data-testid="manage-findings-popover"
        className="!vai-bg-background-dark !vai-border-background-muted vai-border vai-py-3 vai-w-min vai-max-h-[50vh] vai-overflow-y-auto"
      >
        <div className="vai-flex vai-justify-between vai-items-center vai-mb-4">
          <p className="vai-font-inter vai-font-semibold vai-text-foreground-dark">
            <Trans>Manage Findings</Trans>
          </p>
          <FontAwesomeIcon
            className="vai-text-foreground-muted vai-cursor-pointer vai-p-2"
            icon={faXmark}
            onClick={() => setIsOpen(false)}
          />
        </div>

        {findingsEntries.map(([findingType, { findings, annotations }]) => {
          return (
            <Fragment key={findingType}>
              {/* System findings */}
              {findings.map((finding) => (
                <ValidationFinding
                  disabled={isRestrictClinicalInteractions}
                  finding={finding}
                  isPeriapical={activeImage?.detectedImageType === 'PERIAPICAL'}
                  key={finding.id}
                  onChange={handleChangeUserAnnotation}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                />
              ))}

              {/* User annotations */}
              {annotations.map((annotation) => (
                <ValidationAnnotation
                  annotation={annotation}
                  disabled={isRestrictClinicalInteractions}
                  key={annotation.id}
                  onDelete={handleDeleteUserAnnotation}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                />
              ))}
            </Fragment>
          );
        })}
      </PopoverContent>
    </Popover>
  );
};
